import React from 'react';
import type {
  CanvasBlockLayoutChangeMeta,
  CanvasSectionBlockLayout,
  HomepageDesignSystemSettings,
  HomepageSectionSettings,
} from './sectionRegistry';
import type { EnquiryFormData, SelectedVase } from '../../types';
import SectionElementLayer from './SectionElementLayer';
import {
  resolveSectionMinHeightPx,
} from './layoutEngine/sectionSizing';
import { isImmersiveCanvasSection } from '../../lib/stageLayoutRules';

interface CanvasSectionProps {
  sectionId?: string;
  settings?: HomepageSectionSettings;
  designSystemOverride?: HomepageDesignSystemSettings;
  enquiryEmailTo?: string;
  onSelectVase?: (vase: SelectedVase) => void;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
  onNavigateToPage?: (href: string, meta?: { pageKey?: string }) => void;
  adminPreview?: {
    enabled: boolean;
    viewport?: 'desktop' | 'tablet' | 'mobile';
    interactionMode?: 'none' | 'layout' | 'scale' | 'image';
    isImageAdjustMode?: boolean;
    activeAdjustBlockId?: string | null;
    onExitImageAdjust?: () => void;
    previewMode?: 'edit' | 'interact' | 'immersive';
    showPlacementGrid?: boolean;
    showGapGuides?: boolean;
    selectedBlockId?: string | null;
    selectedBlockIds?: string[];
    styleOverrides?: Record<string, Record<string, unknown> | undefined>;
    layoutOverrides?: Record<string, CanvasSectionBlockLayout | undefined>;
    onSelectBlock?: (blockId: string | null) => void;
    onOpenBlockContent?: (blockId: string, options?: { galleryItemId?: string }) => void;
    onBlockLayoutChange?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onPreviewBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout | null,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onCommitBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onBlockScaleChange?: (blockId: string, scale: { x?: number; y?: number }) => void;
    onDeleteBlock?: (blockId: string) => void;
    onPreviewBlockStyle?: (blockId: string, styleOverride: Record<string, unknown> | null) => void;
    onCommitBlockStyle?: (
      blockId: string,
      styleOverride: Record<string, unknown> | null,
      commitViewport?: 'desktop' | 'tablet' | 'mobile'
    ) => void;
    onTextAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onBlockAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onProductCardDebugSnapshot?: (blockId: string | null, snapshot: Record<string, unknown> | null) => void;
    onConnectedBlockDebugSnapshot?: (snapshot: Record<string, unknown> | null) => void;
  };
}

const CANVAS_FLOOR_ROWS = 6;
const CANVAS_DEFAULT_ROWS = 12;
const IMMERSIVE_STAGE_REFERENCE_WIDTH_PX = 1280;
const IMMERSIVE_STAGE_REFERENCE_HEIGHT_PX = 900;
const IMMERSIVE_STAGE_MIN_SCALE = 0.78;
const ADMIN_PREVIEW_DEVICE_WIDTH: Record<'mobile' | 'tablet', number> = {
  mobile: 375,
  tablet: 768,
};

function resolveAdminPreviewDeviceWidth(
  viewport: 'desktop' | 'tablet' | 'mobile' | undefined
): number | null {
  if (viewport === 'mobile' || viewport === 'tablet') {
    return ADMIN_PREVIEW_DEVICE_WIDTH[viewport];
  }
  return null;
}

function isStepTrackerOnlyCanvas(settings: HomepageSectionSettings | undefined): boolean {
  const blocks = settings?.canvas?.blocks;
  return Array.isArray(blocks) && blocks.length > 0 && blocks.every((block) => block.type === 'site-progress');
}

const CanvasSection: React.FC<CanvasSectionProps> = ({
  sectionId,
  settings,
  designSystemOverride,
  enquiryEmailTo,
  onSelectVase,
  onEnquirySubmit,
  onNavigateToPage,
  adminPreview,
}) => {
  const isAdminPreview = Boolean(adminPreview?.enabled);
  const immersiveViewportRef = React.useRef<HTMLElement | null>(null);
  const [immersiveViewportSize, setImmersiveViewportSize] = React.useState({ width: 0, height: 0 });
  const minHeight = React.useMemo(
    () => {
      return resolveSectionMinHeightPx(settings, {
        floorRows: CANVAS_FLOOR_ROWS,
        fallbackRows: CANVAS_DEFAULT_ROWS,
      });
    },
    [settings]
  );
  const presentation = settings?.canvas?.presentation ?? 'default';
  const immersiveFitScale =
    !isAdminPreview &&
    presentation === 'immersive' &&
    immersiveViewportSize.width > 0 &&
    immersiveViewportSize.height > 0
      ? Math.min(
          1,
          immersiveViewportSize.width / IMMERSIVE_STAGE_REFERENCE_WIDTH_PX,
          immersiveViewportSize.height / IMMERSIVE_STAGE_REFERENCE_HEIGHT_PX
        )
      : 1;
  const immersiveDisplayScale =
    !isAdminPreview && presentation === 'immersive'
      ? Math.max(IMMERSIVE_STAGE_MIN_SCALE, immersiveFitScale)
      : 1;

  React.useEffect(() => {
    if (presentation !== 'immersive' || isAdminPreview) {
      setImmersiveViewportSize((current) =>
        current.width === 0 && current.height === 0 ? current : { width: 0, height: 0 }
      );
      return;
    }

    const node = immersiveViewportRef.current;
    if (!node) return;

    const updateViewportSize = () => {
      const rect = node.getBoundingClientRect();
      const nextSize = {
        width: Math.max(0, Math.round(rect.width)),
        height: Math.max(0, Math.round(rect.height)),
      };
      setImmersiveViewportSize((current) =>
        current.width === nextSize.width && current.height === nextSize.height
          ? current
          : nextSize
      );
    };

    updateViewportSize();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => updateViewportSize());
    observer.observe(node);
    return () => observer.disconnect();
  }, [isAdminPreview, presentation]);

  if (!isAdminPreview && presentation !== 'immersive' && isStepTrackerOnlyCanvas(settings)) {
    return (
      <section className="relative h-0 overflow-visible">
        <SectionElementLayer
          sectionId={sectionId}
          settings={settings}
          designSystemOverride={designSystemOverride}
          enquiryEmailTo={enquiryEmailTo}
          onSelectVase={onSelectVase}
          onEnquirySubmit={onEnquirySubmit}
          onNavigateToPage={onNavigateToPage}
          adminPreview={adminPreview}
          className=""
        />
      </section>
    );
  }

  if (presentation === 'immersive') {
    const stageSection = { type: 'canvas' as const, settings };
    const isStage = isImmersiveCanvasSection(stageSection);
    const immersiveStageScale = immersiveDisplayScale;
    const immersiveStageStyle: React.CSSProperties | undefined =
      !isAdminPreview && immersiveStageScale < 1
        ? {
            width: `${100 / immersiveStageScale}%`,
            height: `${100 / immersiveStageScale}%`,
            transform: `scale(${immersiveStageScale})`,
            transformOrigin: 'top center',
            willChange: 'transform',
          }
        : undefined;
    const adminDeviceWidth = isAdminPreview
      ? resolveAdminPreviewDeviceWidth(adminPreview?.viewport)
      : null;

    return (
      <section
        ref={adminDeviceWidth ? undefined : immersiveViewportRef}
        className={
          adminDeviceWidth
            ? 'relative h-full min-h-0 w-full overflow-hidden bg-black'
            : 'relative h-[100dvh] max-h-[100dvh] min-h-[100dvh] w-full overflow-hidden bg-black'
        }
        data-stage-section={isStage ? 'true' : undefined}
        data-builder-preview-viewport={adminPreview?.viewport ?? undefined}
      >
        <div className="relative flex h-full w-full items-start justify-center overflow-hidden">
          <div
            ref={adminDeviceWidth ? immersiveViewportRef : undefined}
            className={
              adminDeviceWidth
                ? 'relative h-full shrink-0 overflow-hidden'
                : 'relative h-full w-full overflow-hidden'
            }
            style={adminDeviceWidth ? { width: adminDeviceWidth, maxWidth: '100%' } : undefined}
            data-builder-viewport-frame={adminDeviceWidth ? adminPreview?.viewport : undefined}
          >
            <div className="relative h-full w-full overflow-hidden" style={immersiveStageStyle}>
              <SectionElementLayer
                sectionId={sectionId}
                settings={settings}
                designSystemOverride={designSystemOverride}
                enquiryEmailTo={enquiryEmailTo}
                onSelectVase={onSelectVase}
                onEnquirySubmit={onEnquirySubmit}
                onNavigateToPage={onNavigateToPage}
                adminPreview={adminPreview}
                className=""
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full">
      <div className="relative" style={{ minHeight: `${minHeight}px` }}>
        <SectionElementLayer
          sectionId={sectionId}
          settings={settings}
          designSystemOverride={designSystemOverride}
          enquiryEmailTo={enquiryEmailTo}
          onSelectVase={onSelectVase}
          onEnquirySubmit={onEnquirySubmit}
          onNavigateToPage={onNavigateToPage}
          adminPreview={adminPreview}
          className=""
        />
      </div>
    </section>
  );
};

export default CanvasSection;
